import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";

import { captureClient, type RoomAudioGateInput } from "./captureClient";

export const captureKeys = {
  all: ["capture"] as const,
  timeline: (sessionKey: string, date: string) =>
    ["capture", sessionKey, "timeline", date] as const,
  nodes: (sessionKey: string) => ["capture", sessionKey, "nodes"] as const,
};

const NODES_REFETCH_MS = 30_000;

/** Only today polls. */
export const useCaptureTimeline = (
  sessionKey: string,
  date: string,
  isToday: boolean,
) =>
  useSuspenseQuery({
    queryKey: captureKeys.timeline(sessionKey, date),
    queryFn: () => captureClient.timeline(date),
    refetchInterval: isToday ? 30_000 : false,
  });

/** Polls; `capture_node` notification frames also invalidate this key. */
const captureNodesQueryOptions = (sessionKey: string) => ({
  queryKey: captureKeys.nodes(sessionKey),
  queryFn: () => captureClient.nodes(),
  refetchInterval: NODES_REFETCH_MS,
});

export const useCaptureNodes = (sessionKey: string) =>
  useSuspenseQuery(captureNodesQueryOptions(sessionKey));

export const useCaptureNodesQuery = (sessionKey: string) =>
  useQuery(captureNodesQueryOptions(sessionKey));

export const useSetRoomAudioGate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      roomUuid,
      ...gate
    }: RoomAudioGateInput & { roomUuid: string }) =>
      captureClient.setRoomAudioGate(roomUuid, gate),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: captureKeys.all }),
        queryClient.invalidateQueries({ queryKey: ["rooms"] }),
      ]);
    },
  });
};

export const useSetRoomCapture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      roomUuid,
      enabled,
    }: {
      roomUuid: string;
      enabled: boolean;
    }) => captureClient.setRoomCapture(roomUuid, enabled),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: captureKeys.all }),
        queryClient.invalidateQueries({ queryKey: ["rooms"] }),
      ]);
    },
  });
};

export const useEnrollDevice = () =>
  useMutation({
    mutationFn: captureClient.enrollDevice,
    meta: { handled: true },
  });
